/**
 * HeroThicknessModal — Informs users that Hero fire-resistant glass has
 * different minimum thicknesses depending on the glass height.
 *
 * Height ranges:
 *   ≤ 2500 mm  →  22 mm
 *   2501–3000 mm  →  24 mm
 *   3001–3500 mm  →  28 mm
 *
 * Visual: Three glass panes side-by-side at increasing heights,
 * with dashed lines marking height boundaries.
 *
 * Shown every time "Hero-Fire" manufacturer is selected.
 * Dismissed/reset when switching to another manufacturer.
 */
export default class HeroThicknessModal {
  constructor(bridge) {
    this.bridge = bridge;
    this.selectedRange = null; // 'small' | 'medium' | 'large' | null
    this.badgeEl = null;

    this.ranges = [
      { key: 'small',  label: '≤ 2500 mm',     thickness: 22, paneH: 130 },
      { key: 'medium', label: '2501 – 3000 mm', thickness: 24, paneH: 168 },
      { key: 'large',  label: '3001 – 3500 mm', thickness: 28, paneH: 210 },
    ];

    this._buildModal();
    this._bindEvents();

    // Listen for manufacturer changes
    bridge.onChange((filterName, value) => {
      if (filterName === 'manufacturer') {
        this._onManufacturerChange(value);
      }
    });
  }

  // ─── DOM Construction ────────────────────────────────────────

  _buildModal() {
    const overlay = document.createElement('div');
    overlay.className = 'hero-overlay hidden';
    overlay.id = 'hero-overlay';

    const modal = document.createElement('div');
    modal.className = 'hero-modal';

    // Header (no close button — user must select a range and confirm)
    const header = document.createElement('div');
    header.className = 'hero-modal-header';
    header.innerHTML = `
      <div class="hero-modal-title-group">
        <i class="fas fa-exclamation-circle hero-modal-icon"></i>
        <h2 class="hero-modal-title">Hinweis — Hero Brandschutzglas</h2>
      </div>
    `;
    modal.appendChild(header);

    // Body text
    const body = document.createElement('div');
    body.className = 'hero-modal-body';
    body.innerHTML = `
      <p>Hero Brandschutzglas hat <strong>unterschiedliche Mindestdicken</strong> je nach
      Glashöhe. Je höher das Glas, desto dicker muss es sein. Bitte wählen Sie den
      passenden Höhenbereich, damit Dicke und Preis korrekt berechnet werden.</p>
    `;
    modal.appendChild(body);

    // Glass diagram — 3 panes side-by-side, bottom-aligned
    const diagram = document.createElement('div');
    diagram.className = 'hero-glass-diagram';

    // Panes row (flex, align to bottom)
    const panes = document.createElement('div');
    panes.className = 'hero-glass-panes';

    // Dashed lines at the top of pane 1 and pane 2
    // These are positioned relative to the panes container bottom
    panes.innerHTML = `
      <div class="hero-height-line" style="bottom:${this.ranges[0].paneH}px"></div>
      <div class="hero-height-line" style="bottom:${this.ranges[1].paneH}px"></div>
    `;

    this.ranges.forEach(range => {
      const option = document.createElement('button');
      option.className = 'hero-glass-option';
      option.dataset.range = range.key;

      option.innerHTML = `
        <div class="hero-glass-pane" style="height:${range.paneH}px">
          <div class="hero-pane-shine"></div>
          <div class="hero-pane-content">
            <span class="hero-pane-height">${range.label}</span>
            <span class="hero-pane-thick">Dicke: ${range.thickness} mm</span>
          </div>
        </div>
        <div class="hero-glass-check">
          <i class="fas fa-check"></i>
        </div>
      `;

      option.addEventListener('click', () => this._selectRange(range));
      panes.appendChild(option);
    });

    diagram.appendChild(panes);

    // Floor line
    const floor = document.createElement('div');
    floor.className = 'hero-glass-floor';
    diagram.appendChild(floor);

    modal.appendChild(diagram);

    // Footer with consent button
    const footer = document.createElement('div');
    footer.className = 'hero-modal-footer';
    footer.innerHTML = `
      <button class="hero-confirm-btn" id="hero-confirm">
        <i class="fas fa-check"></i>
        <span>Verstanden</span>
      </button>
    `;
    modal.appendChild(footer);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    this.overlay = overlay;
    this.modal = modal;
    this.panesContainer = panes;
  }

  // ─── Events ──────────────────────────────────────────────────

  _bindEvents() {
    // Confirm button — only way to close (requires a selected range)
    this.overlay.querySelector('#hero-confirm')
      .addEventListener('click', () => {
        if (this.selectedRange) {
          this.close();
        }
      });

    // Block backdrop clicks — modal cannot be dismissed by clicking outside
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        e.stopPropagation();
        // Shake the modal to signal the user must interact with it
        this.modal.classList.add('hero-modal-shake');
        setTimeout(() => this.modal.classList.remove('hero-modal-shake'), 400);
      }
    });

    // Block Escape key — modal cannot be dismissed with keyboard
    this._escHandler = (e) => {
      if (e.key === 'Escape' && !this.overlay.classList.contains('hidden')) {
        e.preventDefault();
        this.modal.classList.add('hero-modal-shake');
        setTimeout(() => this.modal.classList.remove('hero-modal-shake'), 400);
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  // ─── Manufacturer Change ─────────────────────────────────────

  _onManufacturerChange(value) {
    if (value === 'Hero-Fire') {
      this.open();
    } else {
      this._reset();
    }
  }

  // ─── Range Selection ─────────────────────────────────────────

  _selectRange(range) {
    this.selectedRange = range.key;

    // Highlight selected pane
    this.panesContainer.querySelectorAll('.hero-glass-option').forEach(c => {
      c.classList.toggle('selected', c.dataset.range === range.key);
    });

    // Enable confirm button
    const confirmBtn = this.overlay.querySelector('#hero-confirm');
    confirmBtn.classList.add('enabled');

    // Show badge
    this._showBadge(range.thickness);
  }

  // ─── Thickness Badge ─────────────────────────────────────────

  _showBadge(thickness) {
    this._removeBadge();

    const badge = document.createElement('div');
    badge.className = 'hero-thickness-badge';
    badge.innerHTML = `
      <i class="fas fa-ruler-vertical hero-badge-icon"></i>
      <span>Hero: min. <strong>${thickness} mm</strong></span>
    `;

    const canvas = document.querySelector('.col-center');
    if (canvas) {
      canvas.appendChild(badge);
      this.badgeEl = badge;
      // Trigger entrance animation
      requestAnimationFrame(() => badge.classList.add('visible'));
    }
  }

  _removeBadge() {
    if (this.badgeEl) {
      this.badgeEl.remove();
      this.badgeEl = null;
    }
  }

  // ─── Reset ───────────────────────────────────────────────────

  _reset() {
    this.selectedRange = null;
    this._removeBadge();

    // Clear selections
    if (this.panesContainer) {
      this.panesContainer.querySelectorAll('.hero-glass-option')
        .forEach(c => c.classList.remove('selected'));
    }

    // Disable confirm button
    const confirmBtn = this.overlay?.querySelector('#hero-confirm');
    if (confirmBtn) confirmBtn.classList.remove('enabled');
  }

  // ─── Open / Close ────────────────────────────────────────────

  open() {
    // Reset visuals when reopening
    this._reset();

    this.overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }
}
